import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 } }),
};

function ServiceCard({ s, i }) {
  return (
    <motion.div
      custom={i}
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      data-testid={`service-card-${s.id}`}
      className="group relative flex flex-col p-8 lg:p-10 bg-card border border-border hover:border-primary/50 transition-colors duration-500 clip-frame"
    >
      <div className="flex items-start justify-between mb-6">
        <div>
          <span className="text-xs tracking-[0.2em] uppercase text-primary">{s.duration}</span>
          <h3 className="font-serif text-3xl lg:text-4xl mt-2 text-foreground">{s.name}</h3>
        </div>
        <ArrowUpRight className="w-6 h-6 text-muted-foreground group-hover:text-primary group-hover:rotate-45 transition-all duration-500" />
      </div>
      <p className="text-muted-foreground font-light leading-relaxed mb-8">{s.description}</p>
      <ul className="space-y-3 mb-10">
        {s.features.map((f, k) => (
          <li key={k} className="flex items-center gap-3 text-sm text-foreground/80">
            <Check className="w-4 h-4 text-primary shrink-0" /> {f}
          </li>
        ))}
      </ul>
      <div className="mt-auto flex items-center justify-between pt-6 border-t border-border">
        <span className="font-serif text-2xl text-primary" data-testid={`service-price-${s.id}`}>{s.price}</span>
        <a href="#book" data-testid={`service-book-${s.id}`} className="text-sm tracking-wide text-foreground hover:text-primary transition-colors nav-link">
          Reserve &rarr;
        </a>
      </div>
    </motion.div>
  );
}

export default function Services() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data.services)).catch(() => {});
  }, []);

  const auto = services.filter((s) => s.category === "auto");
  const home = services.filter((s) => s.category === "home");

  return (
    <section id="services" className="py-24 lg:py-32 max-w-[1400px] mx-auto px-6 lg:px-10" data-testid="services-section">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">What we do</p>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-foreground leading-none">
            Services crafted<br /><span className="italic text-primary">with obsession.</span>
          </h2>
        </div>
        <p className="text-muted-foreground font-light max-w-sm">
          From full interior restorations to white-glove home care — every service is executed to a standard most call excessive. We call it Platinum.
        </p>
      </div>

      <Tabs defaultValue="auto" className="w-full">
        <TabsList className="bg-secondary border border-border mb-12 h-auto p-1" data-testid="services-tabs">
          <TabsTrigger value="auto" data-testid="tab-auto" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-sm tracking-wide px-8 py-3">
            Auto Detailing
          </TabsTrigger>
          <TabsTrigger value="home" data-testid="tab-home" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-sm tracking-wide px-8 py-3">
            Home Interiors
          </TabsTrigger>
        </TabsList>

        <TabsContent value="auto">
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
            {auto.map((s, i) => <ServiceCard key={s.id} s={s} i={i} />)}
          </div>
        </TabsContent>
        <TabsContent value="home">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {home.map((s, i) => <ServiceCard key={s.id} s={s} i={i} />)}
          </div>
        </TabsContent>
      </Tabs>
    </section>
  );
}
